// WHAT: Quick access bar component for dashboard navigation
// WHY:  Provides quick navigation to important modules with categorized icons
// DOMAIN: Core → Dashboard → Layout
// DEPENDENCIES: React Router, Card component, Lucide icons
// RISKS:
//   - NOTE: Quick access items are hardcoded - may not match user preferences
//   - NOTE: Icon mapping may not handle missing icons gracefully
//   - NOTE: Grid layout may not work well on very small screens
//   - NOTE: Navigation paths are hardcoded - may break if routes change

import type { QuickAccessItem } from "../../types/dashboard.types";

import {
  Activity,
  Briefcase,
  Code,
  Home,
  MessageSquare,
  Settings,
  TrendingUp,
  Zap,
  Target,
  Calendar,
  BarChart3,
} from "lucide-react";
import { type FC } from "react";
import { useNavigate } from "react-router-dom";

import { Card } from "@/components/ui";
import { cn } from "@/shared/utils";

/**
 * WHAT:
 *   Definition of all available quick access links organized by category.
 *
 * WHY:
 *   Provides centralized configuration for quick navigation items.
 *
 * NOTE: Items are hardcoded - consider making configurable
 */
const QUICK_ACCESS_ITEMS: QuickAccessItem[] = [
  // Health
  {
    category: "Health",
    description: "Oura health metrics",
    icon: "Activity",
    id: "health-oura",
    label: "Oura",
    path: "/nexus/health/oura",
  },
  {
    category: "Health",
    description: "Body composition",
    icon: "Activity",
    id: "health-withings",
    label: "Withings",
    path: "/nexus/health/withings",
  },
  {
    category: "Health",
    description: "Nutrition tracking",
    icon: "Activity",
    id: "health-yazio",
    label: "Yazio",
    path: "/nexus/health/yazio",
  },

  // Productivity
  {
    category: "Productivity",
    description: "Time tracking",
    icon: "Calendar",
    id: "productivity-timetracq",
    label: "TimeTracQ",
    path: "/timetracq",
  },
  {
    category: "Productivity",
    description: "Daily sessions",
    icon: "Target",
    id: "productivity-miracleflow",
    label: "MiracleFlow",
    path: "/miracle-flow",
  },
  {
    category: "Productivity",
    description: "Task reminders",
    icon: "Zap",
    id: "productivity-reminders",
    label: "Reminders",
    path: "/reminders",
  },

  // Admin
  {
    category: "Admin",
    description: "Project roadmap",
    icon: "BarChart3",
    id: "admin-roadmap",
    label: "Roadmap",
    path: "/roadmap",
  },
  {
    category: "Admin",
    description: "Code tools",
    icon: "Code",
    id: "admin-scripts",
    label: "Scripts",
    path: "/scripts",
  },

  // Investments
  {
    category: "Investments",
    description: "Portfolio",
    icon: "TrendingUp",
    id: "investments-parqet",
    label: "Parqet",
    path: "/investments/parqet",
  },

  // LLM
  {
    category: "LLM",
    description: "AI chat",
    icon: "MessageSquare",
    id: "llm-chatgpt",
    label: "ChatGPT",
    path: "/llm/chatgpt",
  },
  {
    category: "LLM",
    description: "Local AI",
    icon: "MessageSquare",
    id: "llm-ollama",
    label: "Ollama",
    path: "/llm/ollama",
  },

  // Settings
  {
    category: "System",
    description: "App settings",
    icon: "Settings",
    id: "settings",
    label: "Settings",
    path: "/settings",
  },
];

/**
 * WHAT:
 *   Icon mapping for quick access items.
 *
 * WHY:
 *   Maps icon names to React components.
 *
 * NOTE: Missing icons will cause runtime errors
 */
const iconMap: Record<string, FC<{ className?: string }>> = {
  Activity,
  BarChart3,
  Briefcase,
  Calendar,
  Code,
  Home,
  MessageSquare,
  Settings,
  Target,
  TrendingUp,
  Zap,
};

/**
 * WHAT:
 *   Quick access bar component that provides navigation shortcuts to important
 *   modules organized in a grid layout.
 *
 * WHY:
 *   Enables users to quickly navigate to frequently used features without
 *   using the sidebar.
 *
 * BEHAVIOR:
 *   - Displays quick access items in responsive grid
 *   - Groups items by category (not displayed, but available for future use)
 *   - Navigates on click
 *   - Shows icons and labels
 *
 * RISKS:
 *   - NOTE: Quick access items are hardcoded - may not match user preferences
 *   - NOTE: Icon mapping may not handle missing icons gracefully
 *   - NOTE: Grid layout may not work well on very small screens
 *   - NOTE: Navigation paths are hardcoded - may break if routes change
 *
 * @returns {JSX.Element} Quick access bar component
 *
 */
export const QuickAccessBar: FC = () => {
  const navigate = useNavigate();

  /**
   * WHAT:
   *   Handles quick access item click.
   *
   * WHY:
   *   Navigates to selected module.
   *
   * @param path - Navigation path to navigate to
   *
   */
  const handleClick = (path: string) => {
    // Reason: navigate() returns void, but ESLint treats it as Promise
    // eslint-disable-next-line sonarjs/void-use -- Reason: navigate() returns void, not Promise
    void navigate(path);
  };

  return (
    <Card className="mb-6" padding="md" variant="default">
      <div className="space-y-4">
        <h3 className="text-sm font-semibold text-gray-100 mb-3">Quick Access</h3>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {QUICK_ACCESS_ITEMS.map((item) => {
            const Icon = iconMap[item.icon ?? "Home"];

            return (
              <button
                aria-label={`Navigate to ${item.label}`}
                className={cn(
                  "flex flex-col items-center gap-2 p-3 rounded-lg",
                  "bg-gray-800/50 border border-gray-700/50",
                  "hover:bg-gray-700/50 hover:border-tracq-bright-blue/50",
                  "transition-all duration-200",
                  "focus:outline-none focus:ring-2 focus:ring-tracq-bright-blue focus:ring-offset-2 focus:ring-offset-gray-900",
                )}
                key={item.id}
                onClick={() => {
                  handleClick(item.path);
                }}
              >
                {Icon ? <Icon className="w-5 h-5 text-tracq-bright-blue" /> : null}
                <span className="text-xs text-gray-300 text-center">{item.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    </Card>
  );
};
